import { diff, isObject } from 'radash';
import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { v4 as uuidv4 } from 'uuid';
import { SCREENSHARE_VIDEO_ID } from '../constants/screenshare';
import { Scene, Scenes, VideoCropPosition } from '../types/RemotionPlayerConfigTypes/Scene';
import { getVideosAndTimeRanges } from './videoRangeUtils';
import {
  getClipBroadcastId,
  getClipFPS,
  getEndForSegment,
  getStartForSegment,
  isLayoutWithVisionDataEnabledPure
} from './clipUtils';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';
import { AlignedRecordings, Clip } from '../types/clip-types';
import { getAlignedRecordingIdFromSpeakerId } from './alignedRecordingUtils';
import { FitType, LayoutType } from '../types/asset-types';
import { selectAlignedRecordings } from '../selectors/contentMetadataStoreSelectors';
import { getAlignedRecordings } from './alignedRecordings';
import { PlayRanges } from '../types/RemotionPlayerConfigTypes/Segment/PlayRange';

const rectifyScene = (
  scene: Scene,
  currentVideoIds: string[],
  isLayoutsEnabled: boolean
): { scene: Scene; hasUpdated: boolean } => {
  const { all_video_ids: sceneAllVidIds } = scene.details;
  const videosMissingInScene = new Set(diff(currentVideoIds, sceneAllVidIds));
  const staleVideosInScene = new Set(diff(sceneAllVidIds, currentVideoIds));

  if (videosMissingInScene.size === 0 && staleVideosInScene.size === 0) {
    return { scene, hasUpdated: false };
  }

  scene.details.all_video_ids = [...currentVideoIds];
  scene.details.visible_video_ids = [
    ...[...videosMissingInScene].filter(id => (isLayoutsEnabled ? id !== SCREENSHARE_VIDEO_ID : true)),
    ...scene.details.visible_video_ids.filter(i => !staleVideosInScene.has(i))
  ];

  if (isObject(scene.details.video_crop_position_map)) {
    staleVideosInScene.forEach(staleId => delete scene.details.video_crop_position_map?.[staleId]);
  }

  return { scene, hasUpdated: true };
};

export const rectifyScenes = ({
  clip,
  isFullFaceRecEnabled,
  areLayoutsEnabledForPreRecordedEvents,
  contentMetadataStoreInstance
}: {
  clip: Clip;
  isFullFaceRecEnabled: boolean;
  areLayoutsEnabledForPreRecordedEvents: boolean;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}): { updatedScenes: Scenes; rectified: boolean } => {
  const oldScenes = structuredClone(clip.asset_metadata.scenes || []);
  if (!oldScenes || oldScenes.length === 0) {
    throw new Error('No scenes found');
  }
  const allVideoIds = getVideosAndTimeRanges({
    clip: clip,
    isFullFaceRecEnabled,
    areLayoutsEnabledForPreRecordedEvents,
    contentMetadataStoreInstance
  }).map(video => video.videoId);
  const isLayoutsEnabled = isLayoutWithVisionDataEnabledPure({
    clip,
    isFullFaceRecEnabled,
    areLayoutsEnabledForPreRecordedEvents
  });

  const updatedScenes = oldScenes.map(scene => rectifyScene(scene, allVideoIds, isLayoutsEnabled));

  const hasUpdatedAnyScene = updatedScenes.some(scene => scene.hasUpdated);

  if (!hasUpdatedAnyScene) {
    // eslint-disable-next-line no-console
    console.log('Scenes are good!');
    return { updatedScenes: oldScenes, rectified: false };
  }

  return { updatedScenes: updatedScenes.map(scene => scene.scene), rectified: true };
};
export const getVideoCropPositionMap = (
  facialRecognitionToDefaultPosition: SpeakerFaceCoordinatesResponse,
  recordings: AlignedRecordings[]
): Record<string, VideoCropPosition> | undefined => {
  return (
    Object.entries(facialRecognitionToDefaultPosition.speaker_coordinates || {}).reduce(
      (acc, [faceId, faceXPosition]) => {
        const faceXPositionNumber = Number(faceXPosition);
        const speakerId = getAlignedRecordingIdFromSpeakerId(faceId, recordings);
        if (!speakerId) {
          return acc;
        }
        const minimumSizeLength = Math.min(faceXPositionNumber, 1 - faceXPositionNumber);
        const startX = faceXPositionNumber - minimumSizeLength;
        const endX = faceXPositionNumber + minimumSizeLength;

        acc[speakerId] = {
          top_left: { x: startX, y: 0 },
          bottom_right: { x: endX, y: 1 }
        };
        return acc;
      },
      {} as Record<string, VideoCropPosition>
    ) || {}
  );
};

// !! TODO @HiteshChoudhary0098 should check if visible ids from composite segment
export const getDefaultSceneData = ({
  clip,
  compositeId,
  facialRecognitionToDefaultPosition,
  isFullFaceRecEnabled,
  areLayoutsEnabledForPreRecordedEvents,
  contentMetadataStoreInstance,
  isSplitsTranslationEnabled
}: {
  clip: Clip;
  compositeId?: string;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  isFullFaceRecEnabled: boolean;
  areLayoutsEnabledForPreRecordedEvents: boolean;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
  isSplitsTranslationEnabled: boolean;
}): Scene => {
  const layout = clip.asset_metadata.layout as LayoutType;
  const fitMode = clip.asset_metadata.fit as FitType;
  const all_video_ids = getVideosAndTimeRanges({
    clip: clip,
    isFullFaceRecEnabled,
    areLayoutsEnabledForPreRecordedEvents,
    contentMetadataStoreInstance
  }).map(video => video.videoId);
  const fps = getClipFPS(clip);
  const broadcastId = getClipBroadcastId(clip);
  const alignedRecordingsFromMetadata = broadcastId
    ? selectAlignedRecordings(broadcastId)(contentMetadataStoreInstance.getState())
    : [];
  const alignedRecordings =
    alignedRecordingsFromMetadata.length > 0
      ? alignedRecordingsFromMetadata
      : broadcastId
        ? getAlignedRecordings({ broadcastId, contentMetadataStoreInstance })
        : [];

  const startTime = compositeId
    ? getStartForSegment({
        clip,
        segmentId: compositeId,
        isSplitsTranslationEnabled
      })
    : clip.asset_metadata.start;
  const endTime = compositeId
    ? getEndForSegment({
        clip,
        segmentId: compositeId,
        isSplitsTranslationEnabled
      })
    : clip.asset_metadata.end;

  return {
    id: uuidv4(),
    name: 'Scene 1',
    details: {
      layout,
      fitMode,
      visible_video_ids: all_video_ids,
      all_video_ids: all_video_ids,
      video_crop_position_map:
        facialRecognitionToDefaultPosition &&
        getVideoCropPositionMap(facialRecognitionToDefaultPosition, alignedRecordings)
    },
    toFrame: endTime * fps,
    fromFrame: startTime * fps
  };
};
/**
 * Maps scenes from original timeline to playable timeline based on play ranges.
 * This is needed for composite segments that have trimmed/deleted sections (playRanges)
 * where scenes need to be adjusted to the new playable timeline.
 *
 * @param scenes - Array of scenes with frame timings relative to original timeline
 * @param playRanges - Array of play ranges defining which parts of the original timeline are actually played
 * @returns Array of scenes with frame timings adjusted to the playable timeline
 */

export function mapScenesToPlayableTimeline(scenes: Scene[] = [], playRanges: PlayRanges): Scene[] {
  if (scenes.length === 0) {
    return [];
  }

  // Helper function to map original frame to playable frame
  const mapOriginalFrameToPlayableFrame = (originalFrame: number): number => {
    let currentPlayableFrame = 0;

    // Walk through play ranges to build mapping
    for (const playRange of playRanges) {
      const rangeDuration = playRange.toFrame - playRange.fromFrame;

      // If our target original frame falls within this play range
      if (originalFrame >= playRange.fromFrame && originalFrame <= playRange.toFrame) {
        const offsetInRange = originalFrame - playRange.fromFrame;
        return currentPlayableFrame + offsetInRange;
      }

      // If our target frame is before this range, it maps to current playable frame
      if (originalFrame < playRange.fromFrame) {
        return currentPlayableFrame;
      }

      // Move past this play range
      currentPlayableFrame += rangeDuration;
    }

    // If original frame is after all play ranges, map to end of playable timeline
    return currentPlayableFrame;
  };

  // Map each scene from original frames to playable frames
  return scenes.map(scene => ({
    ...scene,
    fromFrame: mapOriginalFrameToPlayableFrame(scene.fromFrame),
    toFrame: mapOriginalFrameToPlayableFrame(scene.toFrame)
  }));
}
